/**
 * Investment Editor Store
 *
 * Manages the state of the current investment being edited
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
const initialState = {
    currentInvestment: null,
    currentInvestmentId: null,
    draftInvestment: null,
    isEditing: false,
    hasUnsavedChanges: false,
    isDirty: false,
    validationErrors: [],
    isValid: true,
    lastSavedAt: null,
    autoSaveEnabled: true,
    currentStep: 1,
    totalSteps: 5, // Basic Info, Milestones, Sponsors, Resources/Costs/Benefits, Scoring
};
export const useInvestmentEditorStore = create()(persist((set, get) => ({
    ...initialState,
    // Set current investment
    setCurrentInvestment: (investment) => set({
        currentInvestment: investment,
        currentInvestmentId: investment?.investment_id || null,
    }),
    // Set draft investment
    setDraftInvestment: (draft) => set({
        draftInvestment: draft,
        isDirty: draft !== null,
    }),
    // Update a single field in draft
    updateDraftField: (field, value) => set((state) => ({
        draftInvestment: {
            ...state.draftInvestment,
            [field]: value,
        },
        isDirty: true,
        hasUnsavedChanges: true,
    })),
    // Set editing state
    setIsEditing: (editing) => set({ isEditing: editing }),
    // Set unsaved changes flag
    setHasUnsavedChanges: (hasChanges) => set({ hasUnsavedChanges: hasChanges }),
    // Set dirty flag
    setIsDirty: (dirty) => set({ isDirty: dirty }),
    // Validation errors
    setValidationErrors: (errors) => set({
        validationErrors: errors,
        isValid: errors.length === 0,
    }),
    addValidationError: (error) => set((state) => ({
        validationErrors: [...state.validationErrors, error],
        isValid: false,
    })),
    clearValidationErrors: () => set({
        validationErrors: [],
        isValid: true,
    }),
    // Auto-save
    setLastSavedAt: (date) => set({ lastSavedAt: date }),
    setAutoSaveEnabled: (enabled) => set({ autoSaveEnabled: enabled }),
    // Step navigation
    setCurrentStep: (step) => set({ currentStep: step }),
    nextStep: () => set((state) => ({
        currentStep: Math.min(state.currentStep + 1, state.totalSteps),
    })),
    previousStep: () => set((state) => ({
        currentStep: Math.max(state.currentStep - 1, 1),
    })),
    resetSteps: () => set({ currentStep: 1 }),
    // Utility actions
    startEditing: (investment) => set({
        currentInvestment: investment || null,
        currentInvestmentId: investment?.investment_id || null,
        isEditing: true,
        hasUnsavedChanges: false,
        isDirty: false,
        validationErrors: [],
        isValid: true,
        currentStep: 1,
    }),
    cancelEditing: () => set({
        currentInvestment: null,
        currentInvestmentId: null,
        draftInvestment: null,
        isEditing: false,
        hasUnsavedChanges: false,
        isDirty: false,
        validationErrors: [],
        isValid: true,
        currentStep: 1,
    }),
    saveDraft: () => {
        const draft = get().draftInvestment;
        if (draft && typeof localStorage !== 'undefined') {
            localStorage.setItem('medsuite-investment-draft', JSON.stringify(draft));
            set({
                lastSavedAt: new Date(),
                hasUnsavedChanges: false,
            });
        }
    },
    clearDraft: () => {
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('medsuite-investment-draft');
        }
        set({
            draftInvestment: null,
            isDirty: false,
            hasUnsavedChanges: false,
            lastSavedAt: null,
        });
    },
    reset: () => set(initialState),
}), {
    name: 'medsuite-investment-editor-store',
    partialize: (state) => ({
        // Persist draft and auto-save settings
        draftInvestment: state.draftInvestment,
        autoSaveEnabled: state.autoSaveEnabled,
        currentStep: state.currentStep,
    }),
}));
//# sourceMappingURL=investment-editor.store.js.map